import React, { useEffect } from 'react';
import FileUploader from '../FileUploader/FileUploader';
import useQueue, { dispatchMiddleware } from './useQueue';

export const queueToFiles = (
  fileQueue: FileQueueShape[],
  removeItem: (queueId: any) => any,
  deleteRemoteFile: (queueId: any, fileId?: any) => any
) =>
  fileQueue.map((file) => ({
    ...file,
    name: file.name,
    url: file.url,
    onDelete: file.canRemove
      ? () => removeItem(file.queueId)
      : () => deleteRemoteFile(file.queueId, file.id),
  }));

interface FileQueueShape {
  queueId: string;
  id?: string;
  name: string;
  url?: string;
  loading?: boolean;
  message?: string;
  onDelete?: () => void;
  canRemove?: boolean;
}
// Alias for backwards compatibility
export type InitialFileQueueShape = FileQueueShape;

type FileUploaderContainerProps = {
  /** The text displayed on the button. */
  buttonText: string;
  /** List of valid file types for the html accept property.  */
  validTypes?: string[];
  /** Limit how many files can be uploaded.  */
  maximumAmountOfFiles?: number;
  /** A function for creating the file. It is given a single file object from the html element. It should return a promise. A resolving promise should return an object with the properties {name, id, url}. A failing promise should return a new Error() object. */
  createFile: (file: File) => Promise<unknown>;
  /** A function for deleting the file. It is given the id received from the createFile function. It should return a promise. A resolving promise does not have to return anything. A failing promise should return a new Error() object. */
  deleteFile: (id: string) => Promise<unknown>;
  /** A function which is called every time the file queue changes with the latest queue state. Useful for figuring out if a required upload element is valid. */
  onFileQueueChange: (files: FileQueueShape[]) => void;
  /** The initial queue which is shown before user selects any files.It is a list of File objects accepted by <FileUploader files="" />. */
  initialFileQueue?: FileQueueShape[];
};
const FileUploaderContainer: React.FunctionComponent<FileUploaderContainerProps> = ({
  buttonText,
  createFile,
  deleteFile,
  validTypes,
  maximumAmountOfFiles,
  onFileQueueChange,
  initialFileQueue = [],
}) => {
  const { fileQueue, onChange, deleteRemoteFile, removeItem } = useQueue(
    initialFileQueue,
    createFile,
    deleteFile,
    dispatchMiddleware
  );

  useEffect(() => {
    onFileQueueChange(fileQueue);
  }, [fileQueue]);
  const files = queueToFiles(fileQueue, removeItem, deleteRemoteFile);

  return (
    <FileUploader
      buttonText={buttonText}
      onChange={onChange}
      validTypes={validTypes}
      maximumAmountOfFiles={maximumAmountOfFiles}
      files={files}
    />
  );
};

export default FileUploaderContainer;
